import React from 'react';
import { ArrowRight, Github, Linkedin, Terminal } from 'lucide-react';
import { consultantInfo, stats } from '../mock';

const Hero = () => {
  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-slate-950 bg-grid">
      {/* Ambient blobs */}
      <div className="absolute top-1/3 -left-32 w-[500px] h-[500px] rounded-full bg-emerald-500/8 blur-3xl animate-blob pointer-events-none" />
      <div className="absolute bottom-1/4 -right-32 w-[500px] h-[500px] rounded-full bg-violet-500/8 blur-3xl animate-blob pointer-events-none" style={{ animationDelay: '3s' }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full bg-slate-900/60 blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
        <div className="grid lg:grid-cols-2 gap-16 items-center">

          {/* ── Left ── */}
          <div className="space-y-8">

            {/* Terminal badge */}
            <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-slate-900 border border-slate-700/60 text-xs font-mono">
              <span className="flex gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
              </span>
              <Terminal className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-slate-500">~/akshay $</span>
              <span className="text-emerald-400">Software Engineer</span>
            </div>

            {/* Headline */}
            <div>
              <h1 className="text-5xl sm:text-6xl lg:text-[5.5rem] font-bold leading-[1.05] tracking-tight">
                <span className="text-white">Engineer by </span>
                <span className="gradient-text">Profession.</span>
                <br />
                <span className="text-white">Teacher by </span>
                <span className="gradient-text">Passion.</span>
              </h1>
            </div>

            {/* Description */}
            <p className="text-lg text-slate-400 leading-relaxed max-w-lg">
              {consultantInfo.description}
            </p>

            {/* CTA buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => scrollTo('#experience')}
                className="group inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-base font-bold rounded-xl transition-all duration-200 hover:scale-105 glow-emerald-sm"
              >
                View My Experience
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
              </button>
              <button
                onClick={() => scrollTo('#contact')}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent border border-slate-700 hover:border-slate-500 text-slate-300 hover:text-white text-base font-semibold rounded-xl transition-all duration-200 hover:bg-slate-900/60"
              >
                Get In Touch
              </button>
            </div>

            {/* Social links */}
            <div className="flex items-center gap-6 pt-1">
              <a
                href="https://github.com/akshay-sonalkar"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-slate-500 hover:text-emerald-400 transition-colors duration-200 text-sm font-medium"
              >
                <Github className="w-4 h-4" />
                GitHub
              </a>
              <span className="text-slate-700">·</span>
              <a
                href={`https://${consultantInfo.linkedin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-slate-500 hover:text-emerald-400 transition-colors duration-200 text-sm font-medium"
              >
                <Linkedin className="w-4 h-4" />
                LinkedIn
              </a>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-6 border-t border-slate-800/80">
              {stats.map((stat, i) => (
                <div key={i}>
                  <div className="text-2xl font-bold text-emerald-400 text-glow">{stat.value}</div>
                  <div className="text-xs text-slate-500 mt-1 font-medium uppercase tracking-wider">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right — Photo ── */}
          <div className="relative flex justify-center lg:justify-end">
            <div className="relative">
              {/* Outer glow ring */}
              <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-emerald-500/20 via-transparent to-violet-500/20 blur-2xl animate-pulse-glow" />

              {/* Spinning gradient ring */}
              <div className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-emerald-500/40 to-violet-500/40 animate-spin-slow opacity-30" />

              {/* Photo */}
              <div className="relative rounded-2xl overflow-hidden border border-white/8 shadow-2xl">
                <img
                  src={consultantInfo.image}
                  alt="Akshay Sonalkar — Software Engineer"
                  className="w-full h-auto max-w-[380px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 via-transparent to-transparent" />
              </div>

              {/* Floating open source card */}
              <div className="absolute -top-5 -right-6 glass rounded-xl px-4 py-3 border border-white/8 shadow-xl" style={{ animationDelay: '1.5s' }}>
                <div className="flex items-center gap-2">
                  <span className="text-base">🌐</span>
                  <div className="text-xs font-semibold text-slate-300">Open Source</div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-950 to-transparent pointer-events-none" />
    </section>
  );
};

export default Hero;
